import { useEffect, useState } from 'react';
import { SearchSectionProps } from './SearchSection.types';
import { SearchIcon, XClearSearchIcon } from '../../../icons/SvgIcons';

export function SearchSection({
  searchValue,
  onSearchChange,
  placeholder = 'Search by keyword',
  inputClassName,
}: SearchSectionProps) {
  const [localValue, setLocalValue] = useState(searchValue);

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  // A search below 3 characters is too broad to be useful and floods the
  // backend on every keystroke, so typing only fires `onSearchChange` once
  // the term reaches that length (or is cleared back to empty). Enter/the
  // search icon must honor the exact same gate - otherwise a 1-2 character
  // term silently does nothing while typing but DOES search when the user
  // hits Enter or clicks the icon, which looks like the search box randomly
  // works or doesn't depending on how it's triggered.
  const canSearch = (value: string) => value.length === 0 || value.length >= 3;

  const handleSearchChange = (value: string) => {
    setLocalValue(value);
    if (canSearch(value)) {
      onSearchChange(value);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center flex-1 w-full">
        <div className="relative w-full flex">
          <input
            type="text"
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSearch(localValue)) {
                onSearchChange(localValue);
              }
            }}
            className={`w-full h-12 border border-gray-300 py-2 px-10 rounded-md text-sm leading-5 focus:outline-none hover:border-purple-800 hover:shadow-sm focus:border-purple-700 focus:ring-1 focus:ring-purple-700 focus:shadow-sm placeholder:text-gray-550 placeholder:font-normal placeholder:text-[14px] placeholder:leading-[20px] ${inputClassName}`}
            style={{ fontFamily: 'Inter' }}
          />
          <button
            type="button"
            className="absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer"
            onClick={() => {
              if (canSearch(localValue)) {
                onSearchChange(localValue);
              }
            }}
          >
            <SearchIcon />
          </button>
          {localValue.length > 0 && (
            <button
              type="button"
              className="absolute inset-y-0 right-[13px] pl-3 flex items-center cursor-pointer"
              onClick={() => handleSearchChange('')}
            >
              <XClearSearchIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
